import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';

const STEPS = [
  'Centre Identity',
  'Trust Details',
  'Documents',
  'Operations',
  'Services',
  'Bank Account',
];

const SERVICE_OPTIONS = [
  { value: 'livestream',       label: '📡 Live Streaming (Aartis, Ceremonies)' },
  { value: 'ceremonies',            label: '🪔 Online Ceremony Booking' },
  { value: 'sevaOffering',           label: '🎁 SevaOffering Delivery' },
  { value: 'donations',        label: '💛 Online Donations' },
  { value: 'virtual_darshan',  label: '🕌 Virtual Darshan (VR/360°)' },
  { value: 'festivals',        label: '🎉 Festival Event Streaming' },
];

const CentreRegister = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Centre Identity
    centreName: '', primaryDeity: '', description: '',
    locationAddress: '', locationLat: '', locationLng: '',
    // Step 2: Trust / Authority Details
    trustName: '', headTrusteeName: '', trusteePhone: '', trusteeEmail: '', password: '',
    // Step 3: Verification Documents
    trustRegCertNumber: '', cert80GNumber: '',
    // Step 4: Operational Details
    operatingHours: '', priestName: '', priestPhone: '', hasCameraInfra: '',
    // Step 5: Services to Digitize
    servicesWanted: [], servicesNotes: '',
    // Step 6: Bank Account
    bankName: '', bankAccountNumber: '', bankIfscCode: '', bankAccountHolder: '',
  });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 5000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleServiceToggle = (val) => {
    setFormData(prev => ({
      ...prev,
      servicesWanted: prev.servicesWanted.includes(val)
        ? prev.servicesWanted.filter(s => s !== val)
        : [...prev.servicesWanted, val],
    }));
  };

  const handleNext = (e) => { e.preventDefault(); setStep(s => s + 1); };
  const handleBack = () => setStep(s => s - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/auth/register-centre', {
        centreName: formData.centreName,
        primaryDeity: formData.primaryDeity,
        description: formData.description,
        locationAddress: formData.locationAddress,
        locationLat: formData.locationLat,
        locationLng: formData.locationLng,
        trustName: formData.trustName,
        headTrusteeName: formData.headTrusteeName,
        trusteePhone: formData.trusteePhone,
        trusteeEmail: formData.trusteeEmail,
        password: formData.password,
        trustRegCertNumber: formData.trustRegCertNumber,
        cert80GNumber: formData.cert80GNumber,
        operatingHours: formData.operatingHours,
        priestName: formData.priestName,
        priestPhone: formData.priestPhone,
        hasCameraInfra: formData.hasCameraInfra,
        servicesWanted: formData.servicesWanted,
        servicesNotes: formData.servicesNotes,
        bankName: formData.bankName,
        bankAccountNumber: formData.bankAccountNumber,
        bankIfscCode: formData.bankIfscCode,
        bankAccountHolder: formData.bankAccountHolder,
      });
      if (res.data.success) {
        showToast('success', '🙏 Application submitted! A Super Admin will verify your Trust documents and activate your portal.');
        setTimeout(() => navigate('/login'), 4000);
      } else {
        showToast('error', res.data.message || 'Submission failed. Please try again.');
      }
    } catch (err) {
      showToast('error', err.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inp = { width: '100%', padding: '0.75rem', background: '#1a1a1a', border: '1px solid #333', borderRadius: '0.5rem', color: '#fff', boxSizing: 'border-box', fontSize: '0.875rem' };
  const lbl = { display: 'block', marginBottom: '0.4rem', color: '#E8B84B', fontSize: '0.8rem', fontWeight: 600 };
  const row = { display: 'flex', gap: '1rem' };
  const col = { flex: 1 };
  const btnBack = { flex: 1, padding: '0.75rem', background: 'transparent', border: '1px solid #444', borderRadius: '0.5rem', color: '#888', cursor: 'pointer', fontWeight: 600 };
  const btnNext = { flex: 2, padding: '0.75rem', background: 'linear-gradient(90deg, #E8B84B, #B8923A)', border: 'none', borderRadius: '0.5rem', color: '#000', fontWeight: 800, cursor: 'pointer' };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      background: 'radial-gradient(ellipse at 20% 50%, rgba(212,175,55,0.07) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(255,153,51,0.04) 0%, transparent 50%), #0a0a0a',
      padding: '2rem 1rem',
    }}>
      <div style={{ width: '100%', maxWidth: '600px' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🕌</div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 900, letterSpacing: '0.1em', background: 'linear-gradient(135deg, #E8B84B, #FFD26E, #B8923A)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '0.2rem' }}>Ananda Marga</h1>
          <p style={{ color: '#555', fontSize: '0.7rem', letterSpacing: '0.25em', textTransform: 'uppercase' }}>Centre Onboarding Portal</p>
        </div>

        {/* Step Indicator */}
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1.75rem' }}>
          {STEPS.map((label, idx) => {
            const num = idx + 1;
            const done = step > num;
            const active = step === num;
            return (
              <React.Fragment key={label}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                  <div style={{
                    width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.7rem', fontWeight: 900,
                    background: done ? '#E8B84B' : active ? 'rgba(212,175,55,0.15)' : 'rgba(255,255,255,0.04)',
                    border: `2px solid ${done || active ? '#E8B84B' : '#2a2a2a'}`,
                    color: done ? '#000' : active ? '#E8B84B' : '#444',
                    transition: 'all 0.3s',
                  }}>
                    {done ? '✓' : num}
                  </div>
                  <span style={{ fontSize: '0.55rem', color: active ? '#E8B84B' : done ? '#888' : '#444', marginTop: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'center', lineHeight: 1.2 }}>{label}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div style={{ flex: 1, height: '2px', background: step > num ? '#E8B84B' : '#1e1e1e', marginTop: '13px', transition: 'background 0.3s' }} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Toast */}
        {toast && (
          <div style={{ background: toast.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(248,68,68,0.1)', border: `1px solid ${toast.type === 'success' ? 'rgba(34,197,94,0.3)' : 'rgba(248,68,68,0.3)'}`, borderRadius: '0.5rem', padding: '0.85rem 1rem', color: toast.type === 'success' ? '#4ade80' : '#f87171', marginBottom: '1rem', fontSize: '0.85rem' }}>
            {toast.text}
          </div>
        )}

        <div style={{ padding: '2rem', background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(212,175,55,0.1)', borderRadius: '1rem', backdropFilter: 'blur(12px)' }}>

          {/* ─── STEP 1: Centre Identity ─── */}
          {step === 1 && (
            <form onSubmit={handleNext} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <h2 style={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.2rem' }}>Centre Identity Profile</h2>
                <p style={{ color: '#555', fontSize: '0.78rem', marginBottom: '1rem' }}>Historical and geographical information of the centre.</p>
              </div>
              <div>
                <label style={lbl}>Centre Name *</label>
                <input name="centreName" value={formData.centreName} onChange={handleChange} required placeholder="e.g. Ananda Nagar Master Unit" style={inp} />
              </div>
              <div>
                <label style={lbl}>Centre Type *</label>
                <input name="primaryDeity" value={formData.primaryDeity} onChange={handleChange} required placeholder="e.g. Jagrti, Master Unit, School, Sectorial Office" style={inp} />
              </div>
              <div>
                <label style={lbl}>Historical Significance / Description</label>
                <textarea name="description" rows="3" value={formData.description} onChange={handleChange} placeholder="Brief history and significance of the centre..." style={{ ...inp, resize: 'vertical' }} />
              </div>
              <div>
                <label style={lbl}>Full Address *</label>
                <textarea name="locationAddress" rows="2" value={formData.locationAddress} onChange={handleChange} required placeholder="Street, City, State, PIN Code" style={{ ...inp, resize: 'vertical' }} />
              </div>
              <div style={row}>
                <div style={col}>
                  <label style={lbl}>Latitude (for map routing)</label>
                  <input name="locationLat" value={formData.locationLat} onChange={handleChange} placeholder="e.g. 22.6553" style={inp} />
                </div>
                <div style={col}>
                  <label style={lbl}>Longitude (for map routing)</label>
                  <input name="locationLng" value={formData.locationLng} onChange={handleChange} placeholder="e.g. 88.3519" style={inp} />
                </div>
              </div>
              <button type="submit" style={{ ...btnNext, flex: 'none', marginTop: '0.5rem' }}>Next: Trust Details →</button>
            </form>
          )}

          {/* ─── STEP 2: Trust & Authority Details ─── */}
          {step === 2 && (
            <form onSubmit={handleNext} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <h2 style={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.2rem' }}>Trust & Management Details</h2>
                <p style={{ color: '#555', fontSize: '0.78rem', marginBottom: '1rem' }}>Details of the managing trust / committee for verification.</p>
              </div>
              <div>
                <label style={lbl}>Name of Managing Trust / Committee *</label>
                <input name="trustName" value={formData.trustName} onChange={handleChange} required placeholder="e.g. Ananda Marga Pracaraka Samgha" style={inp} />
              </div>
              <div style={row}>
                <div style={col}>
                  <label style={lbl}>Head Trustee / Chairman Name *</label>
                  <input name="headTrusteeName" value={formData.headTrusteeName} onChange={handleChange} required placeholder="Full name" style={inp} />
                </div>
                <div style={col}>
                  <label style={lbl}>Trustee Contact Phone *</label>
                  <input name="trusteePhone" type="tel" value={formData.trusteePhone} onChange={handleChange} required placeholder="+91 XXXXX XXXXX" style={inp} />
                </div>
              </div>
              <div>
                <label style={lbl}>Official Email Address * <span style={{ color: '#666', fontWeight: 400 }}>(This will be your portal login username)</span></label>
                <input name="trusteeEmail" type="email" value={formData.trusteeEmail} onChange={handleChange} required placeholder="trust@centre.org" style={inp} />
              </div>
              <div>
                <label style={lbl}>Portal Access Password * <span style={{ color: '#666', fontWeight: 400 }}>(Min 6 characters)</span></label>
                <div style={{ position: 'relative' }}>
                  <input 
                    name="password" 
                    type={showPassword ? 'text' : 'password'} 
                    value={formData.password || ''} 
                    onChange={handleChange} 
                    required 
                    placeholder="••••••••" 
                    style={{ ...inp, paddingRight: '2.75rem' }} 
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(v => !v)} 
                    style={{ 
                      position: 'absolute', 
                      right: '0.75rem', 
                      top: '50%', 
                      transform: 'translateY(-50%)', 
                      background: 'none', 
                      border: 'none', 
                      cursor: 'pointer', 
                      color: '#666',
                      fontSize: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0
                    }}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              <div style={{ padding: '0.85rem 1rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '0.5rem', marginTop: '0.25rem' }}>
                <p style={{ color: '#ef4444', fontSize: '0.75rem', fontWeight: 'bold', margin: 0 }}>
                  ⚠️ IMPORTANT WARNING: Please write down and note your Email and Password securely. You will need these credentials to access your centre portal after approval.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button type="button" onClick={handleBack} style={btnBack}>← Back</button>
                <button type="submit" style={btnNext}>Next: Documents →</button>
              </div>
            </form>
          )}

          {/* ─── STEP 3: Verification Documents ─── */}
          {step === 3 && (
            <form onSubmit={handleNext} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <h2 style={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.2rem' }}>Verification Documents</h2>
                <p style={{ color: '#555', fontSize: '0.78rem', marginBottom: '1rem' }}>Required for legitimacy verification. Physical copies will be reviewed by Super Admin.</p>
              </div>
              <div style={{ padding: '0.85rem 1rem', background: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.15)', borderRadius: '0.5rem' }}>
                <p style={{ color: '#E8B84B', fontSize: '0.75rem', margin: 0 }}>
                  📋 Enter your document registration numbers below. The Super Admin team will contact you to collect physical/scanned copies for verification before activating your portal.
                </p>
              </div>
              <div>
                <label style={lbl}>Trust Registration Certificate Number *</label>
                <input name="trustRegCertNumber" value={formData.trustRegCertNumber} onChange={handleChange} required placeholder="e.g. REG/WB/2024/00123 (Societies Registration Act)" style={inp} />
                <p style={{ color: '#555', fontSize: '0.72rem', marginTop: '0.3rem' }}>Under the Societies Registration Act or relevant state trust act</p>
              </div>
              <div>
                <label style={lbl}>80G Tax Exemption Certificate Number <span style={{ color: '#555', fontWeight: 400 }}>(if applicable)</span></label>
                <input name="cert80GNumber" value={formData.cert80GNumber} onChange={handleChange} placeholder="e.g. CIT(E)/80G/2024/00456" style={inp} />
                <p style={{ color: '#555', fontSize: '0.72rem', marginTop: '0.3rem' }}>Required for tax-exempt donation collection</p>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button type="button" onClick={handleBack} style={btnBack}>← Back</button>
                <button type="submit" style={btnNext}>Next: Operations →</button>
              </div>
            </form>
          )}

          {/* ─── STEP 4: Operational Details ─── */}
          {step === 4 && (
            <form onSubmit={handleNext} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <h2 style={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.2rem' }}>Operational Details</h2>
                <p style={{ color: '#555', fontSize: '0.78rem', marginBottom: '1rem' }}>Priest information and livestream infrastructure availability.</p>
              </div>
              <div>
                <label style={lbl}>Daily Operating Hours *</label>
                <input name="operatingHours" value={formData.operatingHours} onChange={handleChange} required placeholder="e.g. 5:00 AM – 12:00 PM & 4:00 PM – 9:00 PM" style={inp} />
              </div>
              <div style={row}>
                <div style={col}>
                  <label style={lbl}>Head Priest (Chief Archaka) *</label>
                  <input name="priestName" value={formData.priestName} onChange={handleChange} required placeholder="Full name" style={inp} />
                </div>
                <div style={col}>
                  <label style={lbl}>Priest Contact Phone *</label>
                  <input name="priestPhone" type="tel" value={formData.priestPhone} onChange={handleChange} required placeholder="+91 XXXXX XXXXX" style={inp} />
                </div>
              </div>
              <div>
                <label style={lbl}>Camera / Internet Infrastructure *</label>
                <select name="hasCameraInfra" value={formData.hasCameraInfra} onChange={handleChange} required style={{ ...inp, appearance: 'auto' }}>
                  <option value="">— Select current status —</option>
                  <option value="yes">Yes — We have cameras and stable internet</option>
                  <option value="partial">Partially — We need some assistance with setup</option>
                  <option value="no">No — We need full infrastructure support</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button type="button" onClick={handleBack} style={btnBack}>← Back</button>
                <button type="submit" style={btnNext}>Next: Services →</button>
              </div>
            </form>
          )}

          {/* ─── STEP 5: Services to Digitize ─── */}
          {step === 5 && (
            <form onSubmit={handleNext} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <h2 style={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.2rem' }}>Services to Digitize</h2>
                <p style={{ color: '#555', fontSize: '0.78rem', marginBottom: '1rem' }}>Select the Ceremonies, Aartis, or festivals you want to digitize first. You can add more later.</p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {SERVICE_OPTIONS.map(opt => {
                  const selected = formData.servicesWanted.includes(opt.value);
                  return (
                    <div
                      key={opt.value}
                      onClick={() => handleServiceToggle(opt.value)}
                      style={{
                        padding: '0.85rem 1rem', borderRadius: '0.5rem', cursor: 'pointer',
                        border: `1px solid ${selected ? '#E8B84B' : '#2a2a2a'}`,
                        background: selected ? 'rgba(212,175,55,0.08)' : 'rgba(255,255,255,0.02)',
                        color: selected ? '#FFD26E' : '#777',
                        fontSize: '0.875rem', fontWeight: selected ? 600 : 400,
                        transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '0.75rem',
                      }}
                    >
                      <span style={{ fontSize: '1.1rem', opacity: selected ? 1 : 0.5 }}>{selected ? '✅' : '⬜'}</span>
                      {opt.label}
                    </div>
                  );
                })}
              </div>
              <div>
                <label style={lbl}>Additional Notes <span style={{ color: '#555', fontWeight: 400 }}>(specific festivals, special Ceremonies, etc.)</span></label>
                <textarea name="servicesNotes" rows="2" value={formData.servicesNotes} onChange={handleChange} placeholder="e.g. We want to stream Durga Ceremony 2025, Daily Sandhya Aarti at 7 PM..." style={{ ...inp, resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button type="button" onClick={handleBack} style={btnBack}>← Back</button>
                <button type="submit" style={btnNext}>Next: Bank Account →</button>
              </div>
            </form>
          )}

          {/* ─── STEP 6: Bank Account ─── */}
          {step === 6 && (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <h2 style={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.2rem' }}>Centre Trust Bank Account</h2>
                <p style={{ color: '#555', fontSize: '0.78rem', marginBottom: '1rem' }}>Dedicated bank account of the Centre Trust for direct donation and Ceremony fee routing.</p>
              </div>
              <div style={{ padding: '0.85rem 1rem', background: 'rgba(255,153,51,0.05)', border: '1px solid rgba(255,153,51,0.15)', borderRadius: '0.5rem' }}>
                <p style={{ color: '#FF9933', fontSize: '0.75rem', margin: 0 }}>
                  🔒 Bank details are encrypted and only used for payout routing. All payouts go directly to the Centre Trust account after platform fee deduction.
                </p>
              </div>
              <div>
                <label style={lbl}>Bank Name *</label>
                <input name="bankName" value={formData.bankName} onChange={handleChange} required placeholder="e.g. State Bank of India" style={inp} />
              </div>
              <div>
                <label style={lbl}>Account Holder Name (as on bank records) *</label>
                <input name="bankAccountHolder" value={formData.bankAccountHolder} onChange={handleChange} required placeholder="e.g. Ananda Marga Pracaraka Samgha" style={inp} />
              </div>
              <div style={row}>
                <div style={col}>
                  <label style={lbl}>Account Number *</label>
                  <input name="bankAccountNumber" value={formData.bankAccountNumber} onChange={handleChange} required placeholder="Account number" style={inp} />
                </div>
                <div style={col}>
                  <label style={lbl}>IFSC Code *</label>
                  <input name="bankIfscCode" value={formData.bankIfscCode} onChange={handleChange} required placeholder="e.g. SBIN0001234" style={{ ...inp, textTransform: 'uppercase' }} />
                </div>
              </div>
              <div style={{ padding: '0.85rem 1rem', background: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.12)', borderRadius: '0.5rem', marginTop: '0.5rem' }}>
                <p style={{ color: '#888', fontSize: '0.75rem', margin: 0 }}>
                  ✅ By submitting, you confirm that all provided information is accurate. A Super Admin will review your Trust documents and provision your Centre Admin Portal within 3–5 business days.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.25rem' }}>
                <button type="button" onClick={handleBack} disabled={loading} style={btnBack}>← Back</button>
                <button type="submit" disabled={loading} style={{ ...btnNext, background: loading ? '#555' : 'linear-gradient(90deg, #FF9933, #d4721a)', color: '#fff', cursor: loading ? 'not-allowed' : 'pointer' }}>
                  {loading ? 'Submitting...' : '🙏 Submit Centre Application'}
                </button>
              </div>
            </form>
          )}

        </div>

        <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
          <a href="/login" style={{ color: '#444', fontSize: '0.78rem', textDecoration: 'none' }}
            onMouseOver={e => e.target.style.color = '#E8B84B'}
            onMouseOut={e => e.target.style.color = '#444'}>
            ← Back to Login
          </a>
        </div>
        <p style={{ textAlign: 'center', marginTop: '0.75rem', color: '#8D8471', fontSize: '0.65rem' }}>Ananda Marga © 2026 — Centre Onboarding Portal</p>
      </div>
    </div>
  );
};

export default CentreRegister;
